import type { RowData } from "@tanstack/react-table";
import { WorkspaceAvatar } from "@design-system/Avatar/WorkspaceAvatar";
import type { DataGridEntityDisplay } from "./cellValueTypes";
import { EmptyCellValue } from "./EmptyCellValue";
import { isWorkspaceValue } from "./listDisplayValues";
import { SearchableText } from "./SearchableText";
import type { DataGridCellProps } from "./types";
import styles from "./cellContent.module.scss";

interface WorkspaceCellProps<
    TData extends RowData,
> extends DataGridCellProps<TData> {
    display: DataGridEntityDisplay;
}

/**
 * Workspace (client) column cell: the square avatar and name, or the avatar
 * alone with the name in the table's shared tooltip for narrow columns.
 */
export const WorkspaceCell = <TData extends RowData>({
    context,
    meta,
    display,
}: Readonly<WorkspaceCellProps<TData>>) => {
    const value = context.getValue();
    if (!isWorkspaceValue(value) || value.name === "") {
        return <EmptyCellValue empty={meta.empty} />;
    }
    if (display === "avatar") {
        return (
            <span
                className={styles.leading}
                data-cell-tooltip={value.name}
                data-cell-tooltip-always=""
            >
                <WorkspaceAvatar workspace={value} size="xs" />
            </span>
        );
    }
    return (
        <span className={styles.inline}>
            <WorkspaceAvatar workspace={value} size="xs" aria-hidden />
            <SearchableText
                text={value.name}
                overflow={meta.overflow}
                table={context.table}
            />
        </span>
    );
};
